import React from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Users } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface PickerMember {
  id: string
  name: string
  email: string
  role?: string
}

interface MemberCheckboxPickerProps {
  members: PickerMember[]
  /** Currently selected member ids (Set or array). */
  selectedIds: Set<string> | string[]
  /** Toggle a single member by id. */
  onToggle: (id: string) => void
  /** Provide to show a select-all / deselect-all toggle. */
  onToggleAll?: () => void
  /** Whether every member is currently selected (drives the toggle label). */
  allSelected?: boolean
  /** Header label; the header is hidden entirely when no label/counter/toggle is shown. */
  label?: string
  /** Show a role Badge on each row (members must carry `role`). */
  showRoleBadge?: boolean
  /** Show a "(selected/total)" counter next to the label. */
  showCounter?: boolean
  /** Text for the empty state; defaults to Common_no_members. */
  emptyLabel?: string
  /** Tailwind max-height class for the scroll container. */
  maxHeightClassName?: string
  className?: string
}

const roleBadgeVariant = (role: string) =>
  role === 'OWNER' ? 'destructive' : role === 'ADMIN' ? 'default' : 'secondary'

/**
 * Presentational multi-select list of members with checkboxes. Selection state
 * and any domain logic (deduping, mapping to a specific model) live in the
 * parent — this component only renders rows and reports toggles by id.
 */
const MemberCheckboxPicker: React.FC<MemberCheckboxPickerProps> = ({
  members,
  selectedIds,
  onToggle,
  onToggleAll,
  allSelected,
  label,
  showRoleBadge = false,
  showCounter = false,
  emptyLabel,
  maxHeightClassName = 'max-h-[200px]',
  className,
}) => {
  const { t } = useTranslation()
  const selected = selectedIds instanceof Set ? selectedIds : new Set(selectedIds)
  const showHeader = !!label || !!onToggleAll || showCounter

  return (
    <div className={cn('space-y-1', className)}>
      {showHeader && (
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
            <Users className="h-3 w-3" />
            {label || t('Common_members')}
            {showCounter && (
              <span className="text-muted-foreground/70">
                ({selected.size}/{members.length})
              </span>
            )}
          </span>
          {onToggleAll && members.length > 0 && (
            <button
              type="button"
              className="text-[11px] text-primary hover:underline"
              onClick={onToggleAll}
            >
              {allSelected ? t('Common_deselect_all') : t('Common_select_all')}
            </button>
          )}
        </div>
      )}
      {members.length === 0 ? (
        <div className="border rounded-md bg-muted/30 text-center text-muted-foreground py-3 text-sm">
          {emptyLabel || t('Common_no_members')}
        </div>
      ) : (
        <div className={cn('border rounded-md overflow-y-auto divide-y', maxHeightClassName)}>
          {members.map((m) => (
            <label
              key={m.id}
              className="flex items-center gap-2.5 px-3 py-1.5 cursor-pointer hover:bg-muted/40"
            >
              <Checkbox checked={selected.has(m.id)} onCheckedChange={() => onToggle(m.id)} />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{m.name}</div>
                <div className="text-xs text-muted-foreground truncate">{m.email}</div>
              </div>
              {showRoleBadge && m.role && (
                <Badge variant={roleBadgeVariant(m.role)} className="shrink-0">
                  {m.role}
                </Badge>
              )}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default MemberCheckboxPicker
